import React, {useEffect} from 'react';
import CustomModal from '../../../../../elements/CustomModal/CustomModal';
import {makeStyles, Typography, ButtonBase} from '@material-ui/core';
import {
  useCurrentConversation,
  getConversationDisplayName,
  useCurrentConversationActions,
  useCurrentConversationState,
  useConversationIdSelected,
} from '../../../chat/utils/conversation';

import {colors} from '../../../../../assets/styles/colors';
import {useFunnelConversationAction} from '../../utils';
import ChatContent from '../../../chat/components/MultiChat/ChatGrid/ChatContent/ChatContent';
import FunnelContactProfile from '../ContactProfile/ContactProfile';
import {setChatDeviceBehaviorService} from '../../../chat/promises';

export type Props = {
  open: boolean,
  onClose: () => void,
};

const useStyles = makeStyles(theme => ({
  modalPaper: {
    width: '100%',
    height: '100%',
    padding: '.5rem',
    borderRadius: '1rem',
    maxHeight: '700px',
    maxWidth: '900px',

    [theme.breakpoints.up('lg')]: {
      maxWidth: '950px',
      maxHeight: '950px',
    },
  },
  modalContent: {
    display: 'flex',
    height: '100%',
  },
  shortNameWrap: {
    background: '#E5DCFC',
    height: 48,
    width: 48,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: '.5rem',
    padding: '.5rem 3rem .5rem .5rem',
    borderBottom: `1px solid ${colors.grey300}`,
  },
  title: {
    color: colors.grey900,
    fontSize: 14,
    fontWeight: 600,
  },
  greyText: {
    color: colors.grey700,
    fontSize: 14,
    fontWeight: 400,
  },
  details: {
    flexGrow: 1,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
  },
  markAsUnreadButton: {
    padding: '.25rem .5rem',
    borderRadius: '.25rem',
    border: `1px solid ${colors.grey700}`,
    cursor: 'pointer',
    fontWeight: 500,
    color: colors.grey700,
  },
  closeChatButton: {
    padding: '.5rem .75rem',
    borderRadius: '.25rem',
    background: `${colors.danger}1a`,
    cursor: 'pointer',
    fontWeight: 500,
    color: colors.danger,
  },
}));

export default function ChatModal(props: Props) {
  const classes = useStyles();
  const {
    currentConversation,
    clearCurrentConversation,
  } = useCurrentConversation();

  const {resetConversationIdSelected} = useConversationIdSelected();
  const {markAsUnread} = useCurrentConversationActions();
  const {
    updateFunnelConversation,
    closeFunnelConversation,
  } = useFunnelConversationAction();

  const {state, toggleConversationState} = useCurrentConversationState();

  const displayName = currentConversation
    ? getConversationDisplayName(currentConversation)
    : '';
  const shortName = displayName.substring(0, 2).toUpperCase();

  const handleMarkAsUnread = () => {
    markAsUnread().then(conversation => {
      if (conversation) {
        updateFunnelConversation(conversation);
      }
    });
  };

  const handleToggleChatState = () => {
    toggleConversationState().then(() => {
      closeFunnelConversation(currentConversation?.id);
      clearCurrentConversation();
    });
    resetConversationIdSelected();
  };

  useEffect(() => {
    if (
      currentConversation &&
      currentConversation.ravenService === 'WHATSAPP_WEB'
    ) {
      const activeWhatsappDeviceId = currentConversation.deviceId;

      if (activeWhatsappDeviceId) {
        setChatDeviceBehaviorService(activeWhatsappDeviceId, {
          service: 'WHATSAPP_WEB',
          deviceId: activeWhatsappDeviceId,
          behavior: 'AVAILABLE',
        }).catch(console.error);

        return () => {
          setChatDeviceBehaviorService(activeWhatsappDeviceId, {
            service: 'WHATSAPP_WEB',
            deviceId: activeWhatsappDeviceId,
            behavior: 'UNAVAILABLE',
          }).catch(console.error);
        };
      }
    }
  }, [currentConversation]);

  return (
    <CustomModal
      open={!!currentConversation}
      handleCloseModal={clearCurrentConversation}
      classes={{
        paper: classes.modalPaper,
      }}
    >
      <header className={classes.header}>
        <div>
          <div className={classes.shortNameWrap}>
            <Typography color="primary" style={{fontWeight: 500, fontSize: 18}}>
              {shortName}
            </Typography>
          </div>
        </div>
        <div className={classes.details}>
          <Typography className={classes.title}>{displayName}</Typography>
          <Typography className={classes.greyText}>
            ID:{currentConversation?.id}
          </Typography>
        </div>
        <div className={classes.actions}>
          <ButtonBase
            className={classes.markAsUnreadButton}
            onClick={handleMarkAsUnread}
          >
            Marcar como no leído
          </ButtonBase>
          {state === 'OPEN' && (
            <ButtonBase
              className={classes.closeChatButton}
              onClick={handleToggleChatState}
            >
              Cerrar Chat
            </ButtonBase>
          )}
        </div>
      </header>
      <div className={classes.modalContent}>
        <ChatContent hideStickyActions />
        <FunnelContactProfile />
      </div>
    </CustomModal>
  );
}
